/**
 * ControlledSeqTour — controlled react-joyride wrapper for multi-stage
 * tours (wizard steps / modals). Each step carries a ``stage`` field;
 * when the tour crosses a stage boundary it pauses, asks the host page
 * to switch UI (open a modal, advance a wizard step) via
 * ``onStageChange``, waits for the DOM to settle, then resumes.
 */
import { useEffect, useRef, useState, useCallback, Component } from "react";
import { Joyride, STATUS, EVENTS, ACTIONS } from "react-joyride";
import { useT } from "@/lib/i18n";

const MOBILE_AUTORUN_BREAKPOINT = 768;
const STAGE_SWITCH_DELAY_MS = 450;

class TourErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) {
    try {
      // eslint-disable-next-line no-console
      console.error("[ControlledSeqTour] suppressed Joyride error:", error);
    } catch { /* ignore */ }
  }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

export default function ControlledSeqTour({
  steps, storageKey, runFlag, onStart, onStageChange, onStop,
}) {
  const t = useT();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const activeRef = useRef(false);

  const begin = useCallback(() => {
    if (activeRef.current) return;
    activeRef.current = true;
    onStart && onStart();
    onStageChange && onStageChange(steps[0].stage);
    setTimeout(() => { setStepIndex(0); setRun(true); }, STAGE_SWITCH_DELAY_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  const stop = useCallback(() => {
    if (!activeRef.current) return;
    activeRef.current = false;
    setRun(false);
    setStepIndex(0);
    try { if (storageKey) localStorage.setItem(storageKey, "1"); }
    catch { /* ignore */ }
    onStop && onStop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Auto-launch on first visit (desktop only).
  useEffect(() => {
    if (!storageKey) return;
    try {
      if (localStorage.getItem(storageKey)) return;
      if (typeof window !== "undefined"
          && window.innerWidth < MOBILE_AUTORUN_BREAKPOINT) return;
    } catch { return; }
    const id = setTimeout(begin, 900);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Manual re-launch (Help button).
  useEffect(() => {
    if (runFlag) begin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runFlag]);

  const handleEvent = useCallback((data) => {
    const { status, type, index, action } = data || {};
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED
        || (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER)) {
      stop();
      return;
    }
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const next = index + (action === ACTIONS.PREV ? -1 : 1);
      if (next < 0 || next >= steps.length) { stop(); return; }
      if (steps[next].stage !== steps[index].stage) {
        onStageChange && onStageChange(steps[next].stage);
        setRun(false);
        setTimeout(() => { setStepIndex(next); setRun(true); }, STAGE_SWITCH_DELAY_MS);
      } else {
        setStepIndex(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, stop]);

  if (!steps || steps.length === 0) return null;

  return (
    <TourErrorBoundary>
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        onEvent={handleEvent}
        options={{
          skipBeacon: true,
          showProgress: true,
          buttons: ["back", "close", "primary", "skip"],
          closeButtonAction: "skip",
          overlayClickAction: false,
          targetWaitTimeout: 3000,
          primaryColor: "#1d4ed8",
          zIndex: 10000,
          arrowColor: "#fff",
          backgroundColor: "#fff",
          textColor: "#1e293b",
        }}
        locale={{
          back: t("tour.back", "Back"),
          close: t("tour.close", "Close"),
          last: t("tour.done", "Done"),
          next: t("tour.next", "Next"),
          skip: t("tour.skip", "Skip"),
        }}
      />
    </TourErrorBoundary>
  );
}
